import * as Yup from "yup";
import { AnyObject, NumberSchema, Reference } from "yup";
import { JOURNEY_EMAIL_ERROR, isEmailFieldName, isValidJourneyEmail } from "./emailValidation";

// key - max, value - min
const minMarks: any = {
  "340": 260,
  "800": 200,
  "1600": 400,
  "36": 1,
  "528": 118,
  "9":  1,
  "120": 0,
  "160": 10,
  "90": 10,
}

const getCurrentYear = () => new Date().getFullYear();

/** Radio value is usually "Yes" / "No"; tolerate boolean true from APIs. */
const isEmploymentEmployedYes = (v: unknown) =>
  v === "Yes" || v === true || String(v ?? "").toLowerCase() === "yes";

/**
 * Names forced required in the employment step UI when employed (see DynamicForm
 * `isEmployeeRequiredField`) plus designation — backend `required` can be false.
 */
const EMPLOYMENT_WHEN_EMPLOYED_NOT_OTHERS_REQUIRED = new Set([
  "employmentCompanyName",
  "employmentCompanyAddress1",
  "employmentCompanyAddress2",
  "employmentCompanyAddress3",
  "employmentLandmark",
  "employmentCompanyPincode",
  "employmentCompanyCity",
  "employmentCompanyState",
  "employmentCompanyCountry",
  "employmentDesignation",
]);

const isTruthyRequiredFlag = (required: unknown) =>
  required === true ||
  required === "true" ||
  String(required ?? "").toLowerCase() === "yes";

const employmentDetailsRequiredWhenEmployed = (field: {
  name: string;
  required?: unknown;
}) =>
  isTruthyRequiredFlag(field.required) ||
  EMPLOYMENT_WHEN_EMPLOYED_NOT_OTHERS_REQUIRED.has(field.name);

const employedAndNotOthers = (emp: unknown, typ: unknown) =>
  isEmploymentEmployedYes(emp) && typ !== "Others";

/** Yes/No radios (entrance exams, etc.) — tolerate boolean true. */
const isYesAnswer = (v: unknown) =>
  v === "Yes" || v === true || String(v ?? "").toLowerCase() === "yes";

const isNoAnswer = (v: unknown) =>
  v === "No" || v === false || String(v ?? "").toLowerCase() === "no";

const normalizeSelectValue = (value: unknown) => {
  if (value && typeof value === "object") {
    const option = value as Record<string, unknown>;
    return String(option.value ?? option.label ?? option.name ?? "").trim();
  }
  return String(value ?? "").trim();
};

const isAdmissionStatusRequiredCondition = (value: unknown) =>
  normalizeSelectValue(value).toLowerCase() !== "undecided";

const toTitleCase = (value: unknown) =>
  String(value ?? "")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const applyConditionalValidation = ({
  shape,
  field,
  fieldKeys,
  activeHorizontalIndex,
  activeVerticalIndex,
  horizontalStepId,
  verticalStepId,
  validationConfig,
}: {
  shape: Record<string, any>;
  field: any;
  fieldKeys: string[];
  activeHorizontalIndex?: number;
  activeVerticalIndex?: number;
  horizontalStepId?: number;
  verticalStepId?: number;
  validationConfig: {
    baseSchema: any;
    conditionField: string;
    condition: (value: unknown) => boolean;
    requiredMessage: string;
  };
}) => {
  const isFieldMatched = fieldKeys.includes(field.name);
  const isHorizontalMatched =
    horizontalStepId === undefined || activeHorizontalIndex === horizontalStepId;
  const isVerticalMatched =
    verticalStepId === undefined || activeVerticalIndex === verticalStepId;

  if (!isFieldMatched || !isHorizontalMatched || !isVerticalMatched) {
    return false;
  }

  shape[field.name] = validationConfig.baseSchema.when(
    validationConfig.conditionField,
    {
      is: validationConfig.condition,
      then: (schema: any) =>
        schema.required(validationConfig.requiredMessage),
      otherwise: (schema: any) => schema.notRequired().nullable(),
    }
  );
  return true;
};

const FUNDS_AND_EXPENSES_CONDITIONAL_FIELDS = [
  "expenseTuitionFees",
  "expenseLivingExpenses",
  "expenseTravelExpenses",
  "expenseOtherExpenses",
  "fundRequestedLoanAmount",
  "fundOwnSources",
  "fundScholarshipAmount",
  "fundOtherIncome",
];

const getReferenceValidationSchema = (field: any) => {
  const fieldName = String(field?.name || "");
  const requiredMessage = `${field.label || field.name} is required`;
  const referenceMatch = fieldName.match(
    /^basicReference(\d+)(FirstName|LastName|Email|PhoneNumber|Mobile)$/i
  );
  const currentReferenceNumber = referenceMatch?.[1];
  const normalizeReferenceDedupeValue = (value: any) =>
    normalizeSelectValue(value).replace(/\s+/g, " ").trim().toLowerCase();
  const getReferenceValue = (
    parent: Record<string, any>,
    referenceNumber: string,
    suffixes: string[],
  ) => {
    const suffix = suffixes.find((item) =>
      parent?.[`basicReference${referenceNumber}${item}`] !== undefined
    );
    return suffix ? parent?.[`basicReference${referenceNumber}${suffix}`] : "";
  };
  const getOtherReferenceNumbers = (parent: Record<string, any>) =>
    Array.from(
      new Set(
        Object.keys(parent || {})
          .map((key) => key.match(/^basicReference(\d+)/i)?.[1])
          .filter(Boolean) as string[]
      )
    ).filter((num) => num !== currentReferenceNumber);
  const isDuplicateReferenceName = (parent: Record<string, any>) => {
    if (!currentReferenceNumber) return false;
    const currentFirstName = normalizeReferenceDedupeValue(
      getReferenceValue(parent, currentReferenceNumber, ["FirstName"])
    );
    const currentLastName = normalizeReferenceDedupeValue(
      getReferenceValue(parent, currentReferenceNumber, ["LastName"])
    );
    if (!currentFirstName || !currentLastName) return false;
    const currentFullName = normalizeReferenceDedupeValue(
      `${currentFirstName} ${currentLastName}`
    );
    return getOtherReferenceNumbers(parent).some((num) => {
      const otherFirstName = normalizeReferenceDedupeValue(
        getReferenceValue(parent, num, ["FirstName"])
      );
      const otherLastName = normalizeReferenceDedupeValue(
        getReferenceValue(parent, num, ["LastName"])
      );
      if (!otherFirstName || !otherLastName) return false;
      const otherFullName = normalizeReferenceDedupeValue(
        `${otherFirstName} ${otherLastName}`
      );
      return otherFullName !== "" && otherFullName === currentFullName;
    });
  };
  const isDuplicateReferenceContact = (
    parent: Record<string, any>,
    suffixes: string[],
  ) => {
    if (!currentReferenceNumber) return false;
    const currentValue = normalizeReferenceDedupeValue(
      getReferenceValue(parent, currentReferenceNumber, suffixes)
    );
    if (!currentValue) return false;
    return getOtherReferenceNumbers(parent).some((num) => {
      const otherValue = normalizeReferenceDedupeValue(
        getReferenceValue(parent, num, suffixes)
      );
      return otherValue !== "" && otherValue === currentValue;
    });
  };
  const withExistingRequired = (schema: any) =>
    isTruthyRequiredFlag(field.required)
      ? schema.required(requiredMessage)
      : schema.notRequired().nullable();

  const basicMatch = fieldName.match(
    /^basicReference\d+(FirstName|LastName|Email|PhoneNumber|Mobile)$/i
  );
  if (basicMatch) {
    const suffix = basicMatch[1].toLowerCase();
    if (suffix === "firstname" || suffix === "lastname") {
      return withExistingRequired(
        Yup.string()
          .transform(toTitleCase)
          .max(25, "Maximum 25 characters allowed.")
          .test(
            "reference-name-format",
            "Only alphabets and spaces are allowed",
            (value) => !value || /^[a-zA-Z ]+$/.test(value)
          )
          .test(
            "reference-full-name-unique",
            "Reference full name already exists for this application",
            function (this: any) {
              return !isDuplicateReferenceName(this.parent || {});
            }
          )
      );
    }
    if (suffix === "email") {
      return withExistingRequired(
        Yup.string()
          .transform((value, originalValue) =>
            originalValue === "" ? undefined : value
          )
	          .email("Please enter a valid email address")
	          .max(40, "Mail ID must not exceed 40 characters")
          .test(
            "reference-email-unique",
            "Reference email already exists for this application",
            function (this: any) {
              return !isDuplicateReferenceContact(this.parent || {}, ["Email"]);
            }
          )
      );
    }
    if (suffix === "phonenumber" || suffix === "mobile") {
      return withExistingRequired(
        Yup.string()
          .test(
            "reference-phone-format",
            "Please enter a valid mobile number",
            (value) => !value || /^(?!.*^(.)\1+$)[6-9]\d{9}$/.test(value)
          )
          .test(
            "reference-mobile-unique",
            "Reference mobile number already exists for this application",
            function (this: any) {
              return !isDuplicateReferenceContact(this.parent || {}, [
                "PhoneNumber",
                "Mobile",
              ]);
            }
          )
      );
    }
  }

  const addressMatch = fieldName.match(
    /^addressReference\d+(AddressLine1|AddressLine2|Locality|Landmark|Area)$/i
  );
  if (addressMatch) {
    const suffix = addressMatch[1].toLowerCase();
    const isOptionalReferenceAddressField =
      suffix === "addressline2" || suffix === "locality" || suffix === "area";
    const isReferenceAlphaNumericSpaceField =
      suffix === "addressline1" ||
      suffix === "addressline2" ||
      suffix === "locality" ||
      suffix === "landmark" ||
      suffix === "area";
    return (isOptionalReferenceAddressField
      ? (schema: any) => schema.notRequired().nullable()
      : withExistingRequired)(
      Yup.string()
        .transform((value, originalValue) =>
          originalValue === "" ? undefined : value
        )
        .max(50, "Maximum 50 characters allowed.")
        .test(
          "reference-address-format",
          isReferenceAlphaNumericSpaceField
            ? "Only alphabets, numbers and space are allowed"
            : "Please enter valid characters",
          (value) =>
            !value ||
            (isReferenceAlphaNumericSpaceField
              ? /^[a-zA-Z0-9 ]*$/.test(value)
              : /^[a-zA-Z0-9.><,\/!@#$%&()\[\]{} \-]*$/.test(value))
        )
    );
  }

  return null;
};

const toNumberForValidation = (value: any) => {
  const candidate =
    value && typeof value === "object" && "value" in value ? value.value : value;
  const normalized = String(candidate ?? "").replace(/,/g, "").trim();
  if (!normalized) return 0;
  const parsed = Number(normalized);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const getCollateralModeFromParent = (parent: Record<string, any>, collateralNumber: string) => {
  const raw =
    parent?.[`collateral${collateralNumber}Type`] ??
    (collateralNumber === "1" ? parent?.collateralType : "");
  const value = normalizeSelectValue(raw).toLowerCase();
  if (value === "307" || value.includes("fixed") || value.includes("fd")) return "fixed";
  if (value === "308" || value.includes("property")) return "property";
  if (value === "309" || value.includes("saving")) return "savings";
  return "";
};

const getCollateralValidationSchema = (field: any) => {
  const fieldName = String(field?.name || "");
  const label = field.label || field.name;
  const requiredMessage = `${label} is required`;
  const requiredString = (schema: any, modes?: string[]) =>
    schema.test("collateral-required-by-type", requiredMessage, function (this: any, value: any) {
      const collateralNumber = fieldName.match(/^collateral(\d*)/i)?.[1] || "1";
      if (
        modes?.length &&
        !modes.includes(getCollateralModeFromParent(this.parent || {}, collateralNumber))
      ) {
        return true;
      }
      return normalizeSelectValue(value) !== "";
    });

  if (/^collateral\d*FdHolderName$/i.test(fieldName) || /^collateral\d*holderName$/i.test(fieldName)) {
    const mode = /FdHolderName$/i.test(fieldName) ? ["fixed"] : ["savings"];
    return requiredString(
      Yup.string()
        .transform(toTitleCase)
        .max(50, "Maximum 50 characters allowed.")
        .test("collateral-holder-name", "Only alphabets and spaces are allowed", (value) => !value || /^[a-zA-Z ]+$/.test(value)),
      mode,
    );
  }

  if (/^collateral\d*FdNumber$/i.test(fieldName)) {
    return requiredString(
      Yup.string()
        .test("collateral-fd-number", "Only integer values are allowed", (value) => !value || /^[0-9]+$/.test(value))
        .test("unique-collateral-fd-number", "FD Number must be unique", function (value) {
          const current = String(value || "").trim();
          return !current || !Object.entries(this.parent || {}).some(
            ([name, other]) =>
              name !== fieldName &&
              /^collateral\d*FdNumber$/i.test(name) &&
              String(other || "").trim() === current,
          );
        }),
      ["fixed"],
    );
  }

  if (/^collateral\d*accountNumber$/i.test(fieldName)) {
    return requiredString(
      Yup.string()
        .transform((value) => String(value ?? "").replace(/,/g, ""))
        .test("collateral-account-number", "Account number is required", (value) => !value || String(value).trim() !== ""),
      ["savings"],
    ).test("unique-collateral-account-number", "Account Number must be unique", function (this: any, value: any) {
      const current = String(value || "").replace(/,/g, "").trim();
      return !current || !Object.entries(this.parent || {}).some(
        ([name, other]) =>
          name !== fieldName &&
          /^collateral\d*accountNumber$/i.test(name) &&
          String(other || "").replace(/,/g, "").trim() === current,
      );
    });
  }

  if (/^collateral\d*(unitPrice|noOfUnits|propertypename|propertytypeName|propertypeName|propertySubTypeName|addressType|pincode|city|state|country)$/i.test(fieldName)) {
    return requiredString(Yup.mixed(), ["property"]);
  }

  if (/^collateral\d*addressLine1$/i.test(fieldName)) {
    return requiredString(
      Yup.string()
        .max(50, "Maximum 50 characters allowed.")
        .test("collateral-address-line1", "Please enter valid characters", (value) => !value || /^[a-zA-Z0-9.><,\/!@#$%&()\[\]{} \-]*$/.test(value)),
      ["property"],
    );
  }

  if (/^collateral\d*addressLine2$/i.test(fieldName)) {
    return Yup.string()
      .transform((value, originalValue) => originalValue === "" ? undefined : value)
      .max(50, "Maximum 50 characters allowed.")
      .test("collateral-address-line2", "Please enter valid characters", (value) => !value || /^[a-zA-Z0-9.><,\/!@#$%&()\[\]{} \-]*$/.test(value))
      .notRequired()
      .nullable();
  }

  if (/^coOwnerCollateral\d+(?:_\d+|\d+)?Name$/i.test(fieldName)) {
    const collateralNumber = fieldName.match(/^coOwnerCollateral(\d+)/i)?.[1] || "1";
    return Yup.string()
      .transform(toTitleCase)
      .max(50, "Maximum 50 characters allowed.")
      .test("collateral-co-owner-name-required", requiredMessage, function (this: any, value) {
        if (!getCollateralModeFromParent(this.parent || {}, collateralNumber)) return true;
        return normalizeSelectValue(value) !== "";
      })
      .test("collateral-co-owner-name-format", "Only alphabets and spaces are allowed", (value) => !value || /^[a-zA-Z ]+$/.test(value));
  }

  if (/^coOwnerCollateral\d+(?:_\d+|\d+)?PhoneNumber$/i.test(fieldName)) {
    const collateralNumber = fieldName.match(/^coOwnerCollateral(\d+)/i)?.[1] || "1";
    return Yup.string()
      .test("collateral-co-owner-phone-required", requiredMessage, function (this: any, value) {
        if (!getCollateralModeFromParent(this.parent || {}, collateralNumber)) return true;
        return normalizeSelectValue(value) !== "";
      })
      .test(
        "collateral-co-owner-phone-format",
        "Please enter a valid mobile number",
        (value) => !value || /^(?!.*^(.)\1+$)[6-9]\d{9}$/.test(value)
      );
  }

  const ownershipMatch = fieldName.match(/^coOwnerCollateral(\d+)(?:_\d+|\d+)?OwnershipPercentage$/i);
  if (ownershipMatch) {
    return Yup.string()
      .test("collateral-owner-percentage-required", requiredMessage, function (this: any, value) {
        if (!getCollateralModeFromParent(this.parent || {}, ownershipMatch[1])) return true;
        return normalizeSelectValue(value) !== "";
      })
      .test("collateral-owner-percentage-format", "Enter up to 2 decimal places", (value) => !value || /^[0-9]+(\.[0-9]{1,2})?$/.test(String(value)))
      .test("collateral-owner-percentage-max", "Ownership percentage cannot exceed 100", (value) => !value || toNumberForValidation(value) <= 100)
      .test("collateral-owner-percentage-total", "Total ownership percentage cannot exceed 100", function () {
        const collateralNumber = ownershipMatch[1];
        const total = Object.entries(this.parent || {})
          .filter(([key]) => new RegExp(`^coOwnerCollateral${collateralNumber}(?:_\\d+|\\d+)?OwnershipPercentage$`, "i").test(key))
          .reduce((sum, [, value]) => sum + toNumberForValidation(value), 0);
        return total <= 100;
      })
      .notRequired()
      .nullable();
  }

  return null;
};

type NullableNumberSchema = NumberSchema<
  number | null | undefined,
  AnyObject,
  undefined,
  ""
>;

export const getValidationSchema = (
  fields: any[],
  activeHorizontalIndex?: number,
  activeVerticalIndex?: number,
  notInList?: any,
  pageType?: string
) => {
  const shape: Record<string, any> = {};


  // Utility function to determine the reference point for sequential validation
  const getRefFor12th = (
    currentType: string | null | undefined,
    previousCourseRef: string
  ) => {
    if (currentType === "12th") {
      return {
        ref: Yup.ref("course1Year") as Reference<number>,
        messageSuffix: "10th Year",
      };
    }
    return {
      ref: Yup.ref(previousCourseRef) as Reference<number>,
      messageSuffix: "Previous Course",
    };
  };

  // if (activeHorizontalIndex === 1 && activeVerticalIndex === 1) {
  //   fields.forEach((field) => {
  //     if (field.name == "permanentAddressLine1" || field.name == "currentAddressLine1") {

  //     }
  //   });
  // }

  // Step-aware validation
  if (activeHorizontalIndex === 1 && activeVerticalIndex === 3) {
    // Academic Details: Only course fields
    fields
      .filter((field) => field.name.startsWith("course"))
      .forEach((field) => {
        if (field.name.includes("Year")) {
          let yearSchema: NullableNumberSchema = Yup.number()
            .transform((value, originalValue) =>
              originalValue === "" ||
              originalValue === null ||
              originalValue === undefined
                ? undefined
                : value
            )
            .typeError(`${field.label} must be a valid year`)
            .integer("Year must be a whole number")
            .min(1950, "Invalid year")
            .max(getCurrentYear(), "Cannot be a future year");

          if (field.name === "course2Year") {
            // Course 2 Year: Needs its own reference check
            yearSchema = yearSchema.when(
              ["course1Year", "course2Type"],
              (values: any[], schema: NullableNumberSchema) => {
                const [course1Year, course2Type] = values;
                
                // Skip validation if course type is Diploma
                if (course2Type === "Diploma") {
                  return schema;
                }
                
                const refData = getRefFor12th(course2Type, "course1Year");

                if ((course1Year || 0) > 0) {
                  const message = `Must be later than ${refData.messageSuffix}`;

                  return schema.moreThan(
                    refData.ref,
                    message
                  ) as NullableNumberSchema;
                }
                return schema;
              }
            );
          } else if (field.name === "course3Year") {
            // Course 3 Year: Needs both course type (for dynamic message) and flexibility logic
            yearSchema = yearSchema.when(
              ["course1Year", "course2Year", "course2Type", "course3Type"],
              (values: any[], schema: NullableNumberSchema) => {
                const [course1Year, course2Year, course2Type, course3Type] =
                  values;

                // Skip validation if course type is Diploma
                if (course3Type === "Diploma") {
                  return schema;
                }

                const refData = getRefFor12th(course3Type, "course2Year");

                // Determine which year to check against (10th or previous)
                const yearToCheckAgainst =
                  refData.ref.path === "course1Year"
                    ? course1Year
                    : course2Year;

                if ((yearToCheckAgainst || 0) > 0) {
                  let messageType = course2Type;
                  if (refData.messageSuffix === "10th Year") {
                    messageType = "10th";
                  }

                  const dynamicMessage = `Must be later than ${messageType} Year`;

                  return schema.moreThan(
                    refData.ref,
                    dynamicMessage
                  ) as NullableNumberSchema;
                }
                return schema;
              }
            );
          } else if (field.name === "course4Year") {
            // Course 4 Year: Needs flexibility logic
            yearSchema = yearSchema.when(
              ["course1Year", "course3Year", "course3Type", "course4Type"],
              (values: any[], schema: NullableNumberSchema) => {
                const [course1Year, course3Year, course3Type, course4Type] =
                  values;

                // Skip validation if course type is Diploma
                if (course4Type === "Diploma") {
                  return schema;
                }

                const refData = getRefFor12th(course4Type, "course3Year");
                const yearToCheckAgainst =
                  refData.ref.path === "course1Year"
                    ? course1Year
                    : course3Year;

                if ((yearToCheckAgainst || 0) > 0) {
                  let messageType = course3Type;
                  if (refData.messageSuffix === "10th Year") {
                    messageType = "10th";
                  }

                  const dynamicMessage = `Must be later than ${messageType} Year`;

                  return schema.moreThan(
                    refData.ref,
                    dynamicMessage
                  ) as NullableNumberSchema;
                }
                return schema;
              }
            );
          }

          if (isTruthyRequiredFlag(field.required)) {
            yearSchema = yearSchema.required(
              `${field.label || field.name} is required`
            ) as NullableNumberSchema;
          } else {
            yearSchema = yearSchema.nullable().optional() as NullableNumberSchema;
          }
          shape[field.name] = yearSchema;
        } else if (/^course\d+Type$/i.test(field.name)) {
          shape[field.name] = isTruthyRequiredFlag(field.required)
            ? Yup.string()
                .trim()
                .required(`${field.label || field.name} is required`)
                .notOneOf(
                  [""],
                  `${field.label || field.name} is required`
                )
            : Yup.string().nullable().optional();
        } else if (
          /^course\d+Cgpa$/i.test(field.name) ||
          /^course\d+MarksCgpa$/i.test(field.name) ||
          /^course\d+MarksPercentage$/i.test(field.name) ||
          /^course\d+MarksObtained$/i.test(field.name) ||
          /^course\d+Marks$/i.test(field.name)
        ) {
          const isCgpaField =
            /Cgpa$/i.test(field.name) || /MarksCgpa$/i.test(field.name);
          let marksSchema = Yup.number()
            .transform((value, originalValue) =>
              originalValue === "" ||
              originalValue === null ||
              originalValue === undefined
                ? undefined
                : value
            )
            .typeError(`${field.label || field.name} must be a number`)
            .min(
              isCgpaField ? 0 : 40,
              isCgpaField
                ? `${field.label || field.name} cannot be negative`
                : `${field.label || field.name} must be at least 40`
            )
            .max(
              isCgpaField ? 10 : 100,
              isCgpaField
                ? `${field.label || field.name} cannot exceed 10`
                : `${field.label || field.name} cannot exceed 100`
            );
          shape[field.name] = isTruthyRequiredFlag(field.required)
            ? marksSchema.required(
                `${field.label || field.name} is required`
              )
            : marksSchema.nullable().optional();
        } else if (field.name.includes("Percentage")) {
          let percentageSchema = Yup.number()
            .transform((value, originalValue) => {
              // keep original value as string for validation
              return originalValue === "" ? null : Number(originalValue);
            })
            .test(
              "no-invalid-chars",
              "Percentage cannot contain +, -, or e",
              (_, ctx) => {
                const input = String(ctx.originalValue || "");
                return !/[+\-eE]/.test(input);
              }
            )
            .test(
              "valid-number-format",
              "Enter a valid percentage (digits & max 2 decimals)",
              (_, ctx) => {
                const input = String(ctx.originalValue || "");
                if (!input) return true;
                return /^\d+(\.\d{1,2})?$/.test(input);
              }
            )
            .nullable()
            .typeError(`${field.label} must be a number`)
            .min(40, `${field.label} must be greater than or equal to 40`)
            .max(100, `${field.label} cannot exceed 100`);

          percentageSchema = isTruthyRequiredFlag(field.required)
            ? percentageSchema.required(
                `${field.label || field.name} is required`
              )
            : percentageSchema.optional();
          shape[field.name] = percentageSchema;
        }
      });

    let finalSchema = Yup.object()
      .shape(shape)
      .test(
        "academic-years-unique",
        "Academic years cannot be the same.",
        function (values) {
          const yearsToCompare: number[] = [
            values.course1Year,
            values.course2Year,
            values.course3Year,
            values.course4Year,
          ]
            .map((val) => Number(val))
            .filter((val) => !isNaN(val) && val > 0);

          if (yearsToCompare.length < 2) {
            return true;
          }

          const uniqueYears = new Set(yearsToCompare);
          return uniqueYears.size === yearsToCompare.length;
        }
      );

    return finalSchema;
  } else if (activeHorizontalIndex === 0 && activeVerticalIndex === 2) {
    // "totalCost"
    shape.totalCost = Yup.number()
      .typeError("Total cost must be a number")
      .nullable()
      .optional()
      .min(1000000, "Total cost must be greater than ₹10,00,000")
      .max(50000000, "Total cost cannot exceed ₹5,00,00,000");

    // "loanAmount"
    shape.loanAmount = Yup.number()
      .typeError("Loan amount must be a number")
      .nullable()
      .optional()
      .min(1000000, "Loan amount must be greater than ₹10,00,000")
      .max(15000000, "Loan amount cannot exceed ₹1,50,00,000")
      .test(
        "loan-amount-not-greater-than-total-cost",
        "Loan amount cannot be greater than total cost",
        function (value) {
          const { totalCost } = this.parent;
          
          // if totalCost is missing or invalid according to its own rules, returning true here to HIDE the loanAmount error.
          if (!totalCost || totalCost < 1000000 || totalCost > 50000000) {
            return true; 
          }

          // only if totalCost is valid, perform the comparison
          if (!value) return true;
          return value <= totalCost;
        }
      );
  } else if (activeHorizontalIndex === 1 && activeVerticalIndex === 4) {
    // Entrance Exams: Conditional validation for core/language fields
    fields.forEach((field) => {
      const reqMsg = (f: any) =>
        `${f?.label || f?.name || "This field"} is required`;

      if (field.name === "coreExamApplied") {
        shape[field.name] = field.required
          ? Yup.string().required(reqMsg(field))
          : Yup.string();
      } else if (/^coreExamName(_\d+)?$/.test(field.name)) {
        shape[field.name] = Yup.string().when("coreExamApplied", {
          is: (v: unknown) => isYesAnswer(v),
          then: (s) => s.required(reqMsg(field)).min(1, reqMsg(field)),
          otherwise: (s) => s.notRequired(),
        });
      } else if (/^coreScoreReceived(_\d+)?$/.test(field.name)) {
        shape[field.name] = Yup.string().when("coreExamApplied", {
          is: (v: unknown) => isYesAnswer(v),
          then: (s) => s.required(reqMsg(field)),
          otherwise: (s) => s.notRequired(),
        });
      } else if (/^coreScoreExpectedOn(_\d+)?$/.test(field.name)) {
        const scoreReceivedField = field.name.replace(
          /^coreScoreExpectedOn/,
          "coreScoreReceived"
        );
        shape[field.name] = Yup.date()
          .typeError(`Please enter a valid ${field.label || field.name}`)
          .when(["coreExamApplied", scoreReceivedField], {
            is: (applied: any, recv: any) =>
              isYesAnswer(applied) && isNoAnswer(recv),
            then: (s) => s.required(reqMsg(field)),
            otherwise: (s) => s.notRequired().nullable(),
          });
      } else if (/^coreTotalMarks(_\d+)?$/.test(field.name)) {
        const suffixMatch = field.name.match(/(_\d+)$/);
        const suffix = suffixMatch ? suffixMatch[0] : "";
        const scoreReceivedField = `coreScoreReceived${suffix}`;
        shape[field.name] = Yup.string().when(["coreExamApplied", scoreReceivedField], {
          is: (applied: any, recv: any) => isYesAnswer(applied) && !isNoAnswer(recv),
          then: (s) => s.required(reqMsg(field)),
          otherwise: (s) => s.notRequired(),
        });
      } else if (/^coreExamScore(_\d+)?$/.test(field.name)) {
        const suffixMatch = field.name.match(/(_\d+)$/);
        const suffix = suffixMatch ? suffixMatch[0] : "";
        const scoreReceivedField = `coreScoreReceived${suffix}`;
        shape[field.name] = Yup.number()
          .transform((value, originalValue) =>
            originalValue === "" || originalValue === null || originalValue === undefined
              ? undefined
              : value
          )
          .nullable()
          .typeError(`${field.label || field.name} must be a number`)
          .when(["coreExamApplied", scoreReceivedField], {
            is: (applied: any, recv: any) =>
              isYesAnswer(applied) && !isNoAnswer(recv),
            then: (schema) =>
              schema
                .required(reqMsg(field))
                .test(
                  "not-greater-than-total-marks",
                  `${field.label || field.name} cannot be greater than total marks`,
                  function (value) {
                    const sm = this.path.match(/(_\d+)$/);
                    const suf = sm ? sm[1] : "";
                    const totalMarksField = `coreTotalMarks${suf}`;
                    const totalMarks = this.parent[totalMarksField];
                    if (value == null || totalMarks == null || totalMarks === "")
                      return true;
                    return Number(value) <= Number(totalMarks);
                  }
                )
                .test(
                  "not-less-than-min-marks",
                  "",
                  function (this: Yup.TestContext, value) {
                    const sm = this.path.match(/(_\d+)$/);
                    const suf = sm ? sm[1] : "";
                    const totalMarksField = `coreTotalMarks${suf}`;
                    const totalMarks = (this.parent as any)[totalMarksField];
                    if (value == null || totalMarks == null || totalMarks === "")
                      return true;
                    const minValue = Number(minMarks?.[totalMarks?.toString()]);
                    if (Number(value) < minValue) {
                      return this.createError({
                        message: `${field.label || field.name} cannot be less than ${minValue}`,
                      });
                    }
                    return true;
                  }
                ),
            otherwise: (schema) => schema.notRequired().nullable(),
          });
      } else if (field.name === "languageExamApplied") {
        shape[field.name] = field.required
          ? Yup.string().required(reqMsg(field))
          : Yup.string();
      } else if (/^languageExamName(_\d+)?$/.test(field.name)) {
        shape[field.name] = Yup.string().when("languageExamApplied", {
          is: (v: unknown) => isYesAnswer(v),
          then: (s) => s.required(reqMsg(field)).min(1, reqMsg(field)),
          otherwise: (s) => s.notRequired(),
        });
      } else if (/^languageScoreReceived(_\d+)?$/.test(field.name)) {
        shape[field.name] = Yup.string().when("languageExamApplied", {
          is: (v: unknown) => isYesAnswer(v),
          then: (s) => s.required(reqMsg(field)),
          otherwise: (s) => s.notRequired(),
        });
      } else if (/^languageScoreExpectedOn(_\d+)?$/.test(field.name)) {
        const scoreReceivedField = field.name.replace(
          /^languageScoreExpectedOn/,
          "languageScoreReceived"
        );
        shape[field.name] = Yup.date()
          .typeError(`Please enter a valid ${field.label || field.name}`)
          .when(["languageExamApplied", scoreReceivedField], {
            is: (applied: any, recv: any) =>
              isYesAnswer(applied) && isNoAnswer(recv),
            then: (s) => s.required(reqMsg(field)),
            otherwise: (s) => s.notRequired().nullable(),
          });
      } else if (/^languageTotalMarks(_\d+)?$/.test(field.name)) {
        const suffixMatch = field.name.match(/(_\d+)$/);
        const suffix = suffixMatch ? suffixMatch[0] : "";
        const scoreReceivedField = `languageScoreReceived${suffix}`;
        shape[field.name] = Yup.string().when(["languageExamApplied", scoreReceivedField], {
          is: (applied: any, recv: any) => isYesAnswer(applied) && !isNoAnswer(recv),
          then: (s) => s.required(reqMsg(field)),
          otherwise: (s) => s.notRequired(),
        });
      } else if (/^languageExamScore(_\d+)?$/.test(field.name)) {
        const suffixMatch = field.name.match(/(_\d+)$/);
        const suffix = suffixMatch ? suffixMatch[0] : "";
        const scoreReceivedField = `languageScoreReceived${suffix}`;
        shape[field.name] = Yup.number()
          .transform((value, originalValue) =>
            originalValue === "" || originalValue === null || originalValue === undefined
              ? undefined
              : value
          )
          .nullable()
          .typeError(`${field.label || field.name} must be a number`)
          .when(["languageExamApplied", scoreReceivedField], {
            is: (applied: any, recv: any) =>
              isYesAnswer(applied) && !isNoAnswer(recv),
            then: (schema) =>
              schema
                .required(reqMsg(field))
                .test(
                  "not-greater-than-total-marks",
                  `${field.label || field.name} cannot be greater than total marks`,
                  function (value) {
                    const sm = this.path.match(/(_\d+)$/);
                    const suf = sm ? sm[1] : "";
                    const totalMarksField = `languageTotalMarks${suf}`;
                    const totalMarks = this.parent[totalMarksField];
                    if (value == null || totalMarks == null || totalMarks === "")
                      return true;
                    return Number(value) <= Number(totalMarks);
                  }
                )
                .test(
                  "not-less-than-min-marks",
                  "",
                  function (this: Yup.TestContext, value) {
                    const sm = this.path.match(/(_\d+)$/);
                    const suf = sm ? sm[1] : "";
                    const totalMarksField = `languageTotalMarks${suf}`;
                    const totalMarks = (this.parent as any)[totalMarksField];
                    if (value == null || totalMarks == null || totalMarks === "")
                      return true;
                    const minValue = Number(minMarks?.[totalMarks?.toString()]);
                    if (Number(value) < minValue) {
                      return this.createError({
                        message: `${field.label || field.name} cannot be less than ${minValue}`,
                      });
                    }
                    return true;
                  }
                ),
            otherwise: (schema) => schema.notRequired().nullable(),
          });
      }
    });
  } else {
    // Default: all required fields (modified)
    fields.forEach((field) => {
      if (
        applyConditionalValidation({
          shape,
          field,
          fieldKeys: FUNDS_AND_EXPENSES_CONDITIONAL_FIELDS,
          activeHorizontalIndex,
          activeVerticalIndex,
          validationConfig: {
            baseSchema: Yup.string(),
            conditionField: "admissionStatus",
            condition: isAdmissionStatusRequiredCondition,
            requiredMessage: `${field.label || field.name} is required`,
          },
        })
      ) {
        return;
      }

      const referenceValidationSchema = getReferenceValidationSchema(field);
      if (referenceValidationSchema) {
        shape[field.name] = referenceValidationSchema;
        return;
      }

      const collateralValidationSchema = getCollateralValidationSchema(field);
      if (collateralValidationSchema) {
        shape[field.name] = collateralValidationSchema;
        return;
      }

      // Special case: eligibility check step (0,1) — university/institute/courseName
      // are NOT required when admissionStatus is "Undecided" (regardless of not-in-list mode)
      if (
        activeHorizontalIndex === 0 &&
        activeVerticalIndex === 1 &&
        (field.name === "university" || field.name === "institute" || field.name === "courseName")
      ) {
        const reqMsg = `${field.label || field.name} is required`;
        shape[field.name] = Yup.mixed().when("admissionStatus", {
          is: (val: any) => val !== "Undecided",
          then: field.required //for university and coursename error fix  
            ? (s: any) =>
                s.test(
                  "autocomplete-obj-required",
                  reqMsg,
                  (value: any) =>
                    value !== null && value !== undefined && value !== ""
                )
            : (s: any) => s.notRequired(),
          otherwise: (s: any) => s.notRequired().nullable(),
        });
        return;
      }
      if (
        pageType === "SUMMARY" &&
        activeHorizontalIndex === 0 &&
        activeVerticalIndex === 0 &&
        (field.name === "terms_conditions" ||
          field.name === "privacy_policy")
      ) {
        shape[field.name] = Yup.boolean().optional();
        return;
      }
      if (
        pageType === "SUMMARY" &&
        activeHorizontalIndex === 0 &&
        activeVerticalIndex === 0 &&
        field.name === "applicantType"
      ) {
        shape[field.name] = Yup.string()
          .test(
            "applicant-type-summary",
            "Please select who is applying",
            (value) => value != null && String(value).trim() !== ""
          )
          .nullable()
          .optional();
        return;
      }
      if (field.name === "applicantDependents") {
        const dependentsSchema = Yup.number()
          .transform((value, originalValue) =>
            originalValue === "" ||
            originalValue === null ||
            originalValue === undefined
              ? undefined
              : value
          )
          .typeError(`${field.label || field.name} must be a number`)
          .min(0, `${field.label || field.name} cannot be negative`)
          .max(10, "Applicant Dependents should not exceed 10");
        shape[field.name] = isTruthyRequiredFlag(field.required)
          ? dependentsSchema.required(`${field.label || field.name} is required`)
          : dependentsSchema.nullable().optional();
        return;
      }
      // Special handling for address year fields
      if (
        field.name === "permanentYearsInCurrentAddress" ||
        field.name === "permanentYearsInCurrentCity" ||
        field.name === "currentYearsInCurrentAddress" ||
        field.name === "currentYearsInCurrentCity" ||
        /^addressCoApplicant\d+(permanent|current)YearsInCurrent(Address|City)$/.test(
          field.name
        )
      ) {
        const maxYears =
          field.name === "permanentYearsInCurrentAddress" ||
          field.name === "permanentYearsInCurrentCity" ||
          /^addressCoApplicant\d+(permanent|current)YearsInCurrent(Address|City)$/.test(
            field.name
          )
            ? 150
            : 999;
        const yearSchema = Yup.number()
          .transform((value, originalValue) =>
            originalValue === "" || originalValue === null || originalValue === undefined
              ? undefined
              : value
          )
          .typeError(`${field.label || field.name} must be a number`)
          .min(0, "Years must be at least 0")
          .max(maxYears, `Years cannot exceed ${maxYears}`);
        shape[field.name] = field.required
          ? yearSchema.required(
              `${field.label || field.name} is required`
            )
          : yearSchema.nullable().optional();
        return;
      }
      // Special handling for LoanDetailsLoanAmount
      if (field.name === "LoanDetailsLoanAmount") {
        const loanAmt = Yup.number()
          .transform((value, originalValue) =>
            originalValue === "" ||
            originalValue === null ||
            originalValue === undefined
              ? undefined
              : value
          )
          .typeError("Loan amount must be a number")
          .min(1000000, "Loan amount must be at least ₹10,00,000")
          .max(15000000, "Loan amount cannot exceed ₹1,50,00,000");
        shape[field.name] = isTruthyRequiredFlag(field.required)
          ? loanAmt.required(
              `${field.label || "Loan amount"} is required`
            )
          : loanAmt.nullable().optional();
        return;
      }
      // Special handling for collateralType and collateralAmount
      if (
        // field.name === "collateralType" ||
        field.name === "collateralAmount"
      ) {
        shape[field.name] = Yup.string().when("collateral", {
          is: (val: any) => val === "Yes",
          then: (schema: any) =>
            // schema.required(`${field.label} is required`), // COMMENTED
            schema
              .notRequired() // Use notRequired for "then" if required is commented
              .test(
                "max-value",
                "Collateral amount cannot exceed 1.5 Crore (15,000,000)",
                (value: any) => {
                  const numValue = Number(value);
                  if (isNaN(numValue)) return true;
                  return numValue <= 15000000;
                }
              ),
          otherwise: (schema: any) => schema.notRequired(),
        });
        return;
      }
      // PAN validation: required and regex (main applicant)
      if (field.name === "pan") {
        const panTransform = (value: unknown) =>
          value ? String(value).trim().toUpperCase().slice(0, 10) : "";
        if (field.required) {
          shape[field.name] = Yup.string()
            .transform(panTransform)
            .required("PAN is required")
            .length(10, "PAN must be exactly 10 characters")
            .matches(/^[A-Z]{5}[0-9]{4}[A-Z]$/, "PAN must be in format AAAAA9999A");
        } else {
          shape[field.name] = Yup.string()
            .transform(panTransform)
            .test(
              "pan-format-optional",
              "PAN must be in format AAAAA9999A",
              (value) =>
                !value ||
                value === "YES" ||
                value === "NO" ||
                (value.length === 10 && /^[A-Z]{5}[0-9]{4}[A-Z]$/.test(value))
            )
            .nullable()
            .optional();
        }
        return;
      }
      // PAN validation: required and regex (co-applicant PAN fields)
      if (/^basicCoApplicant\d+PanNumber$/.test(field.name)) {
        const panTransform = (value: unknown) =>
          value ? String(value).trim().toUpperCase().slice(0, 10) : "";
        if (field.required) {
          shape[field.name] = Yup.string()
            .transform(panTransform)
            .required("PAN is required")
            .length(10, "PAN must be exactly 10 characters")
            .matches(/^[A-Z]{5}[0-9]{4}[A-Z]$/, "PAN must be in format AAAAA9999A");
        } else {
          shape[field.name] = Yup.string()
            .transform(panTransform)
            .test(
              "pan-format-optional",
              "PAN must be in format AAAAA9999A",
              (value) =>
                !value ||
                (value.length === 10 && /^[A-Z]{5}[0-9]{4}[A-Z]$/.test(value))
            )
            .nullable()
            .optional();
        }
        return;
      }

      if (
       /^addressCoApplicant\d+(permanent|current)AddressLine\d+$/i.test(field.name)
      ) {
        shape[field.name] = field.required
          ? Yup.string()
              .trim()
              .min(1, `${field.label || field.name} is required`)
              .max(50, "Maximum 50 characters allowed.")
              .required(`${field.label || field.name} is required`)
          : Yup.string()
              .max(50, "Maximum 50 characters allowed.")
              .nullable()
              .optional();
        return;
      }

      if (
        field.name == "permanentAddressLine1" ||
        field.name == "currentAddressLine1" ||
        field.name == "permanentAddressLine2" ||
        field.name == "currentAddressLine2" ||
        field.name == "permanentAddressLine3" ||
        field.name == "currentAddressLine3"
      ) {
        shape[field.name] = field.required
          ? Yup.string()
              .trim()
              .min(1, `${field.label || field.name} is required`)
              .max(50, "Maximum 50 characters allowed.")
              .required(`${field.label || field.name} is required`)
          : Yup.string()
              .max(50, "Maximum 50 characters allowed.")
              .nullable()
              .optional();
        return;
      }
      if (
        field.name == "employmentCompanyAddress1" ||
        field.name == "employmentCompanyAddress2" ||
        field.name == "employmentCompanyAddress3"
      ) {
        const base = Yup.string()
          .min(1, "Minimum 1 characters required.")
          .max(50, "Maximum 50 characters allowed.");
        shape[field.name] =
          activeHorizontalIndex === 1 && activeVerticalIndex === 2
            ? base.when(["employmentisEmployed", "employmentType"], {
                is: (emp: unknown, typ: unknown) =>
                  employedAndNotOthers(emp, typ),
                then: (s) =>
                  employmentDetailsRequiredWhenEmployed(field)
                    ? (s as any).required(
                        `${field.label} is required`
                      )
                    : (s as any).nullable().optional(),
                otherwise: (s) =>
                  (s as any).notRequired().nullable(),
              })
            : isTruthyRequiredFlag(field.required)
              ? base.required(`${field.label} is required`)
              : base.nullable().optional();
        return;
      }
 if (/^employmentCoApplicant\d+(companyAddress\d+|landmark)$/i.test(field.name)) {
        shape[field.name] = isTruthyRequiredFlag(field.required)
          ? Yup.string()
              .trim()
              .min(1, `${field.label || field.name} is required`)
              .max(50, "Maximum 50 characters allowed.")
              .required(`${field.label || field.name} is required`)
          : Yup.string()
              .max(50, "Maximum 50 characters allowed.")
              .nullable()
              .optional();
        return;
      }

      if (/^employmentCoApplicant\d+(companyAddress\d+|landmark)$/i.test(field.name)) {
        shape[field.name] = isTruthyRequiredFlag(field.required)
          ? Yup.string()
              .trim()
              .min(1, `${field.label || field.name} is required`)
              .max(50, "Maximum 50 characters allowed.")
              .required(`${field.label || field.name} is required`)
          : Yup.string()
              .max(50, "Maximum 50 characters allowed.")
              .nullable()
              .optional();
        return;
      }

      // Validation for employment company name - 2 to 50 characters
      if (field.name === "employmentCompanyName") {
        const base = Yup.string()
          .min(2, "Minimum 2 characters required.")
          .max(50, "Maximum 50 characters allowed.");
        shape[field.name] =
          activeHorizontalIndex === 1 && activeVerticalIndex === 2
            ? base.when(["employmentisEmployed", "employmentType"], {
                is: (emp: unknown, typ: unknown) =>
                  employedAndNotOthers(emp, typ),
                then: (s) =>
                  employmentDetailsRequiredWhenEmployed(field)
                    ? (s as any).required(
                        `${field.label} is required`
                      )
                    : (s as any).nullable().optional(),
                otherwise: (s) =>
                  (s as any).notRequired().nullable(),
              })
            : isTruthyRequiredFlag(field.required)
              ? base.required(`${field.label} is required`)
              : base.nullable().optional();
        return;
      }

      // Validation for employment designation - 2 to 50 characters
      if (field.name === "employmentDesignation") {
        const base = Yup.string().min(1, "Minimum 1 characters required.");
        shape[field.name] =
          activeHorizontalIndex === 1 && activeVerticalIndex === 2
            ? base.when(["employmentisEmployed", "employmentType"], {
                is: (emp: unknown, typ: unknown) =>
                  employedAndNotOthers(emp, typ),
                then: (s) =>
                  employmentDetailsRequiredWhenEmployed(field)
                    ? (s as any).required(
                        `${field.label} is required`
                      )
                    : (s as any).nullable().optional(),
                otherwise: (s) =>
                  (s as any).notRequired().nullable(),
              })
            : isTruthyRequiredFlag(field.required)
              ? base.required(`${field.label} is required`)
              : base.nullable().optional();
        return;
      }

      // Validation for employmentYear - must be between 0 and 60
      if (field.name === "employmentYear") {
        const base = Yup.number()
          .transform((value, originalValue) =>
            originalValue === "" || originalValue === null || originalValue === undefined
              ? undefined
              : value
          )
          .typeError("Must be a valid number")
          .min(0, "Years cannot be negative")
          .max(60, "Employment year cannot be greater than 60")
          .integer("Must be a whole number");
        shape[field.name] =
          activeHorizontalIndex === 1 && activeVerticalIndex === 2
            ? base.when(["employmentisEmployed", "employmentType"], {
                is: (emp: unknown, typ: unknown) =>
                  employedAndNotOthers(emp, typ),
                then: (s) =>
                  field.required
                    ? (s as any).required(
                        `${field.label} is required`
                      )
                    : (s as any).nullable().optional(),
                otherwise: (s) =>
                  (s as any).notRequired().nullable(),
              })
            : field.required
              ? base.required(`${field.label} is required`)
              : base.nullable().optional();
        return;
      }

      // Validation for employmentMonth - must be between 0 and 12
      if (field.name === "employmentMonth") {
        const base = Yup.number()
          .transform((value, originalValue) =>
            originalValue === "" || originalValue === null || originalValue === undefined
              ? undefined
              : value
          )
          .typeError("Must be a valid number")
          .min(0, "Months cannot be negative")
          .max(12, "Employment month cannot be greater than 12")
          .integer("Must be a whole number");
        shape[field.name] =
          activeHorizontalIndex === 1 && activeVerticalIndex === 2
            ? base.when(["employmentisEmployed", "employmentType"], {
                is: (emp: unknown, typ: unknown) =>
                  employedAndNotOthers(emp, typ),
                then: (s) =>
                  field.required
                    ? (s as any).required(
                        `${field.label} is required`
                      )
                    : (s as any).nullable().optional(),
                otherwise: (s) =>
                  (s as any).notRequired().nullable(),
              })
            : field.required
              ? base.required(`${field.label} is required`)
              : base.nullable().optional();
        return;
      }

      // Monthly Obligation - max value 20 (DIY / dynamic form validation)
      if (field.name === "employmentMonthlyObligation") {
        const base = Yup.number()
          .transform((value, originalValue) =>
            originalValue === "" || originalValue === null || originalValue === undefined
              ? undefined
              : value
          )
          .typeError("Monthly Obligation must be a number")
          .min(0, "Monthly Obligation cannot be negative")
          // .max(20, "Monthly Obligation cannot exceed 20");
        shape[field.name] =
          activeHorizontalIndex === 1 && activeVerticalIndex === 2
            ? base.when("employmentisEmployed", {
                is: (v: unknown) => isEmploymentEmployedYes(v),
                then: (s) =>
                  field.required
                    ? (s as any).required(
                        `${field.label} is required`
                      )
                    : (s as any).nullable().optional(),
                otherwise: (s) =>
                  (s as any).notRequired().nullable(),
              })
            : field.required
              ? base.required(`${field.label} is required`)
              : base.nullable().optional();
        return;
      }

      if (field.name === "employmentLandmark") {
        const base = Yup.string()
          .min(1, "Minimum 1 characters required.")
          .max(50, "Maximum 50 characters allowed.");
        shape[field.name] =
          activeHorizontalIndex === 1 && activeVerticalIndex === 2
            ? base.when(["employmentisEmployed", "employmentType"], {
                is: (emp: unknown, typ: unknown) =>
                  employedAndNotOthers(emp, typ),
                then: (s) =>
                  employmentDetailsRequiredWhenEmployed(field)
                    ? (s as any).required(`${field.label} is required`)
                    : (s as any).nullable().optional(),
                otherwise: (s) =>
                  (s as any).notRequired().nullable(),
              })
            : isTruthyRequiredFlag(field.required)
              ? base.required(`${field.label} is required`)
              : base.nullable().optional();
        return;
      }

      if (field.name === "employmentCompanyPincode") {
        const base = Yup.number()
          .transform((value, originalValue) =>
            originalValue === "" || originalValue === null || originalValue === undefined
              ? undefined
              : value
          )
          .typeError(`${field.label} must be a number`)
          .integer("Pincode must be a whole number")
          .min(100000, "Pincode must be 6 digits")
          .max(999999, "Pincode must be 6 digits");
        shape[field.name] =
          activeHorizontalIndex === 1 && activeVerticalIndex === 2
            ? base.when(["employmentisEmployed", "employmentType"], {
                is: (emp: unknown, typ: unknown) =>
                  employedAndNotOthers(emp, typ),
                then: (s) =>
                  employmentDetailsRequiredWhenEmployed(field)
                    ? (s as any).required(`${field.label} is required`)
                    : (s as any).nullable().optional(),
                otherwise: (s) =>
                  (s as any).notRequired().nullable(),
              })
            : isTruthyRequiredFlag(field.required)
              ? base.required(`${field.label} is required`)
              : base.nullable().optional();
        return;
      }

      if (field.name === "employmentCompanyCountry") {
        const base = Yup.string()
          .notOneOf([""], `Please select a ${field.label}`);
        shape[field.name] =
          activeHorizontalIndex === 1 && activeVerticalIndex === 2
            ? base.when(["employmentisEmployed", "employmentType"], {
                is: (emp: unknown, typ: unknown) =>
                  employedAndNotOthers(emp, typ),
                then: (s) =>
                  employmentDetailsRequiredWhenEmployed(field)
                    ? (s as any).required(`${field.label} is required`)
                    : (s as any).nullable().optional(),
                otherwise: (s) =>
                  (s as any).notRequired().nullable(),
              })
            : isTruthyRequiredFlag(field.required)
              ? base.required(`${field.label} is required`)
              : base.nullable().optional();
        return;
      }

      // Validation for employment company city - 2 to 50 characters
      if (field.name === "employmentCompanyCity") {
        const base = Yup.string()
          .min(2, "Minimum 2 characters required.")
          .max(50, "Maximum 50 characters allowed.");
        shape[field.name] =
          activeHorizontalIndex === 1 && activeVerticalIndex === 2
            ? base.when(["employmentisEmployed", "employmentType"], {
                is: (emp: unknown, typ: unknown) =>
                  employedAndNotOthers(emp, typ),
                then: (s) =>
                  employmentDetailsRequiredWhenEmployed(field)
                    ? (s as any).required(
                        `${field.label} is required`
                      )
                    : (s as any).nullable().optional(),
                otherwise: (s) =>
                  (s as any).notRequired().nullable(),
              })
            : isTruthyRequiredFlag(field.required)
              ? base.required(`${field.label} is required`)
              : base.nullable().optional();
        return;
      }

      // Validation for employment company state - 2 to 50 characters
      if (field.name === "employmentCompanyState") {
        const base = Yup.string()
          .min(2, "Minimum 2 characters required.")
          .max(50, "Maximum 50 characters allowed.");
        shape[field.name] =
          activeHorizontalIndex === 1 && activeVerticalIndex === 2
            ? base.when(["employmentisEmployed", "employmentType"], {
                is: (emp: unknown, typ: unknown) =>
                  employedAndNotOthers(emp, typ),
                then: (s) =>
                  employmentDetailsRequiredWhenEmployed(field)
                    ? (s as any).required(
                        `${field.label} is required`
                      )
                    : (s as any).nullable().optional(),
                otherwise: (s) =>
                  (s as any).notRequired().nullable(),
              })
            : isTruthyRequiredFlag(field.required)
              ? base.required(`${field.label} is required`)
              : base.nullable().optional();
        return;
      }

      // Validation for firstName and lastName - max 26 characters
      if (
        field.name === "firstName" ||
        field.name === "lastName" ||
        field.name === "studentDetailsFirstName" ||
        field.name === "studentDetailsLastName" ||
        /^basicCoApplicant\d+FirstName$/.test(field.name) ||
        /^basicCoApplicant\d+LastName$/.test(field.name)
      ) {
        shape[field.name] = field.required
          ? Yup.string()
              .required(`${field.label} is required`)
              .max(26, "Maximum 26 characters allowed.")
              
          : Yup.string()
              .max(26, "Maximum 26 characters allowed.")
              
              .nullable()
              .optional();
        return;
      }

      // Validation for fatherName and motherName - max 150 characters
      if (
        field.name === "fatherName" ||
        field.name === "motherName" ||
        /^basicCoApplicant\d+FatherName$/.test(field.name) ||
        /^basicCoApplicant\d+MotherName$/.test(field.name)
      ) {
        const parentSchema = Yup.string().max(
          150,
          "Maximum 150 characters allowed."
        );
        shape[field.name] = field.required
          ? parentSchema.required(`${field.label} is required`)
          : parentSchema.nullable().optional();
        return;
      }

      if (
        ["fatherFirstName", "fatherLastName", "motherFirstName", "motherLastName"].includes(
          field.name
        )
      ) {
        const parentNameSchema = Yup.string()
          .max(50, "Maximum 50 characters allowed.")
          .matches(/^[a-zA-Z ]*$/, "Only alphabets and spaces are allowed");
        shape[field.name] = field.required
          ? parentNameSchema.required(`${field.label} is required`)
          : parentNameSchema.nullable().optional();
        return;
      }

      // Required Loan Amount (Applicant Additional Details): the generic
      // number-field rule below already requires a value, but a plain
      // `.required()` treats 0 as present/valid, so 0 needs its own check.
      if (field.name === "loanLoanAmount") {
        const loanAmountSchema = Yup.number()
          .transform((value, originalValue) =>
            originalValue === "" || originalValue === null || originalValue === undefined
              ? undefined
              : value
          )
          .typeError("Please enter the required loan amount.")
          .moreThan(0, "Loan amount must be greater than 0.");
        shape[field.name] = isTruthyRequiredFlag(field.required)
          ? loanAmountSchema.required("Please enter the required loan amount.")
          : loanAmountSchema.nullable().optional();
        return;
      }

      if (field.required) {
        switch (field.type) {
          case "number":
            shape[field.name] = Yup.number()
              .transform((value, originalValue) =>
                originalValue === "" ||
                originalValue === null ||
                originalValue === undefined
                  ? undefined
                  : value
              )
              .typeError(`${field.label} must be a number`)
              .required(`${field.label} is required`);
            break;
          case "phone":
            shape[field.name] = Yup.string()
              .required(`${field.label} is required`)
              .matches(/^[0-9]{10}$/, "Mobile number must be 10 digits");
            break;
          case "email":
            shape[field.name] = Yup.string()
              .required(`${field.label} is required`)
              .email("Please enter a valid email address")
              .max(40, "Mail ID must not exceed 40 characters");
            break;
          case "date":
            shape[field.name] = Yup.date()
              .typeError(`Please enter a valid ${field.label}`)
              .required(`${field.label} is required`);
            break;
          case "dropdown":
            shape[field.name] = Yup.string()
              .required(`${field.label} is required`)
              .notOneOf([""], `Please select a ${field.label}`);
            break;
          case "autocomplete":
            if (notInList?.includes(field.label)) {
              shape[field.name] = Yup.mixed()
                .required(`${field.label} is required`)
                .test(
                  "not-in-list-required",
                  `When Not In List is selected, please enter the ${field.label}`,
                  (value: any) => {
                    const text =
                      value == null || value === ""
                        ? ""
                        : typeof value === "string"
                          ? value
                          : value?.label ?? value?.value ?? "";
                    return (text ?? "").toString().trim().length > 0;
                  }
                )
                // .test(
                //   "has-alphanumeric",
                //   "Only letters, numbers and ! @ # . / + are allowed",
                //   (value: any) => {
                //     if (!value) return true;
                //     const text =
                //       typeof value === "string"
                //         ? value
                //         : value?.label || value?.value;
                //     if (!text) return true;
                //     return /^[a-zA-Z0-9 !@#./+]+$/.test(text);
                //   }
                // );
            } else {
              shape[field.name] = Yup.mixed()
                .required(`${field.label} is required`)
                .test(
                  "autocomplete-required",
                  `Please select a ${field.label}`,
                  (value) =>
                    value !== null &&
                    value !== undefined &&
                    value !== ""
                )
                // .test(
                //   "has-alphanumeric",
                //   "Only letters, numbers and ! @ # . / + are allowed",
                //   (value: any) => {
                //     if (!value) return true;
                //     const text =
                //       typeof value === "string"
                //         ? value
                //         : value?.label || value?.value;
                //     if (!text) return true;
                //     return /^[a-zA-Z0-9 !@#./+]+$/.test(text);
                //   }
                // );
            }
            break;
          case "radio":
            shape[field.name] = Yup.string().required(
              `${field.label} is required`
            );
            break;
          case "checkbox":
            shape[field.name] = Yup.boolean()
              .oneOf([true], `${field.label} must be checked`)
              .required(`${field.label} is required`);
            break;
          default:
            shape[field.name] = Yup.string()
              .trim()
              .required(`${field.label} is required`);
            break;
        }
      } else {
        switch (field.type) {
          case "number":
            shape[field.name] = Yup.number()
              .transform((value, originalValue) =>
                originalValue === "" || originalValue === null || originalValue === undefined
                  ? undefined
                  : value
              )
              .typeError(`${field.label} must be a number`)
              .nullable()
              .optional();
            break;
          case "phone":
            shape[field.name] = Yup.string()
              .matches(/^[0-9]{10}$/, "Mobile number must be 10 digits")
              .nullable()
              .optional();
            break;
          case "email":
            shape[field.name] = Yup.string()
              .email("Please enter a valid email address")
              .max(40, "Mail ID must not exceed 40 characters")
              .nullable()
              .optional();
            break;
          case "date":
            shape[field.name] = Yup.date()
              .typeError(`Please enter a valid ${field.label}`)
              .nullable()
              .optional();
            break;
          case "dropdown":
            shape[field.name] = Yup.string().nullable().optional();
            break;
          case "radio":
            shape[field.name] = Yup.string().nullable().optional();
            break;
          default:
            shape[field.name] = Yup.string().nullable().optional();
            break;
        }
      }
    });
  }

  applyPincodeLengthValidation(fields, shape);
  applyEmailFormatValidation(fields, shape);

  return Yup.object().shape(shape);
};

// Every pincode field (applicant, co-applicant, reference, collateral, employment...)
// must be exactly 6 digits when filled; empty values are left to the field's own
// required rule.
const PINCODE_FIELD_REGEX = /pin\s*code$/i;

const applyPincodeLengthValidation = (
  fields: any[],
  shape: Record<string, any>,
) => {
  (fields || []).forEach((field: any) => {
    if (!field?.name || !PINCODE_FIELD_REGEX.test(field.name)) return;

    const existingSchema = shape[field.name] ?? Yup.string().nullable().optional();
    if (typeof existingSchema?.test !== "function") return;

    shape[field.name] = existingSchema.test(
      "pincode-length",
      `${field.label || "Pincode"} must be 6 digits`,
      (value: any) => {
        if (value === undefined || value === null || String(value).trim() === "") {
          return true;
        }
        return /^\d{6}$/.test(String(value).trim());
      },
    );
  });
};

// Every email field (applicant, co-applicant, reference) must be a valid address
// without "+" or "-". Empty values are left to the field's own required rule.
const applyEmailFormatValidation = (
  fields: any[],
  shape: Record<string, any>,
) => {
  (fields || []).forEach((field: any) => {
    if (!field?.name || !(field.type === "email" || isEmailFieldName(field.name))) return;

    const existingSchema = shape[field.name] ?? Yup.string().nullable().optional();
    if (typeof existingSchema?.test !== "function") return;

    shape[field.name] = existingSchema.test(
      "journey-email-format",
      JOURNEY_EMAIL_ERROR,
      (value: any) =>
        value === undefined ||
        value === null ||
        String(value).trim() === "" ||
        isValidJourneyEmail(value),
    );
  });
};
